import type { IPropertyBag } from '@deity-io/di-data-basics';
import { Box, Button, Divider, FlexLayout, H4, Icon, Input } from '@deity-io/portal-ui';
import { TabItem, Tabs } from 'components';
import { RequestExplorerActionTypes, RequestExplorerContext } from 'contexts/RequestExplorer';
import type { EntityTab } from 'contracts/interfaces';
import { get, set } from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';
import { useExecuteRequestMutation } from 'services/graphql/generated/hooks';
import { replaceHandleBars } from 'utils/handlebarReplacement';
import type { AuthenticationInput } from '../../../services/graphql/generated/schemas';
import type { IHttpMethodType } from '../EndpointSettings/Properties';
import { Method } from '../EndpointSettings/Properties';
import LogConsole from './Tabs/LogConsole';
import RequestBody from './Tabs/RequestBody';
import RequestHeaders from './Tabs/RequestHeaders';
import RequestParams from './Tabs/RequestParams';
import RequestVariables from './Tabs/RequestVariables';
import ResponseBody from './Tabs/ResponseBody';
import ResponseHeaders from './Tabs/ResponseHeaders';
import ResponseStructure from './Tabs/ResponseStructure';

interface RequestExplorerProps {
  locked?: boolean;
  availableVariableValues?: IPropertyBag;
}

const RequestExplorer: FC<RequestExplorerProps> = ({ locked, availableVariableValues }) => {
  const [executeRequest, { data: executeRequestResult, loading: executingRequest }] = useExecuteRequestMutation();

  const ctx = useContext(RequestExplorerContext);

  if (!ctx) {
    return <>RequestExplorerContextProvider required</>;
  }

  const requestTabs: EntityTab[] = [
    {
      label: 'Params',
      isActive: true,
      content: <RequestParams url={ctx.state.url} />,
      keepAlive: true
    },
    {
      label: 'Headers',
      content: <RequestHeaders />,
      keepAlive: true
    },
    {
      label: 'Body',
      content: <RequestBody />,
      keepAlive: true
    },

    {
      label: 'Variables',
      content: <RequestVariables availableVariableValues={availableVariableValues} />
    },
    {
      label: 'Settings',
      content: <Box>Settings</Box>
    }
  ];

  const responseTabs: EntityTab[] = [
    {
      label: 'Body',
      isActive: true,
      content: ctx.state.response ? (
        <ResponseBody />
      ) : (
        <Box
          width="100%"
          style={{
            minHeight: '100px',
            textAlign: 'center',
            borderStyle: 'solid',
            borderColor: '#D4D4D4',
            borderWidth: '1px',
            paddingTop: '20px',
            margin: '10px'
          }}
        >
          Hit send to get a response
        </Box>
      )
    },

    {
      label: 'Headers',
      disabled: ctx.state.response === undefined,
      content: <ResponseHeaders />
    },
    {
      label: 'Structure',
      disabled: ctx.state.response === undefined,
      content: <ResponseStructure />
    },
    {
      label: 'Console',
      keepAlive: true,
      content: <LogConsole />
    }
  ];

  const sendRequest = () => {
    const variables: IPropertyBag = {};

    ctx.state.variables.forEach(variable => {
      if (variable.value !== undefined) {
        set(variables, variable.key, variable.value);
      } else {
        const value = get(availableVariableValues, variable.key);
        if (value !== undefined) {
          set(variables, variable.key, value);
        }
      }
    });

    let authentication: AuthenticationInput | undefined;

    if (ctx.state.authentication) {
      const serializedAuthData = replaceHandleBars(JSON.stringify(ctx.state.authentication), variables);

      authentication = {
        data: serializedAuthData,
        type: ctx.state.authentication.type
      };
    }
    const requestSettings = {
      uuid: ctx.state.sessionUuid,
      method: ctx.state.method,
      url: replaceHandleBars(ctx.state.url, variables),
      headers: ctx.state.headers.map(header => [
        replaceHandleBars(header.key, variables),
        replaceHandleBars(header.value, variables)
      ]),
      body: ctx.state.body ? replaceHandleBars(ctx.state.body, variables) : undefined,
      ...(authentication && { authentication })
    };
    executeRequest({
      variables: requestSettings
    }).then(result => {
      ctx.dispatch({
        type: RequestExplorerActionTypes.SET_RESPONSE,
        payload: {
          headers: result.data?.executeRequest.headers.map(rec => ({ key: rec[0], value: rec[1] })),
          resultCode: result.data?.executeRequest.resultCode,
          resultBody: result.data?.executeRequest.resultBody
        }
      });
    });
  };

  return (
    <FlexLayout flexDirection="column" p="xl" style={{ margin: '10px', padding: '10px' }}>
      <H4 key="header" mb="lg" fontWeight="semiBold" fontSize="xl">
        RequestExplorer
      </H4>
      <FlexLayout key="urlBox" width="full" flexDirection="row" flexWrap="nowrap" alignItems="center">
        <Method
          key="method"
          style={{ width: '130px' }}
          method={ctx.state.method}
          onChange={(path, value) => {
            ctx.dispatch({ type: RequestExplorerActionTypes.UPDATE_METHOD, payload: value as IHttpMethodType });
          }}
          isEditable={locked !== true}
        />
        <Input
          key="url"
          fullWidth
          label="Url"
          type="text"
          name="host"
          id="host"
          value={ctx.state.url}
          onChange={e => {
            let valid = true;
            try {
              if ((e.target.value as string).trim().length > 0) {
                const info = new URL(e.target.value);
              }
            } catch (err) {
              valid = false;
            }
            ctx.dispatch({ type: RequestExplorerActionTypes.UPDATE_URL as const, payload: e.target.value });
          }}
          placeholder="Enter request URL"
          disabled={locked}
          autoComplete="off"
          error={!ctx.state.validUrl}
        />
        <Button
          key="send"
          style={{ justifySelf: 'stretch', height: 'auto' }}
          endIcon={<Icon icon="arrowRight" />}
          onClick={sendRequest}
          disabled={executingRequest}
        >
          Send
        </Button>
      </FlexLayout>
      <Divider key="topDivider" />
      <Tabs key="requestTabs">
        {requestTabs?.map(tab => (
          <TabItem label={tab.label} key={tab.label} isActive={tab.isActive ?? false} keepAlive={tab.keepAlive}>
            {tab.content}
          </TabItem>
        ))}
      </Tabs>
      <Divider key="bottomDivider" />
      <Tabs
        key="responseTabs"
        badge={
          ctx.state.response ? <Box css={{ alignItems: 'center' }}>Status : {ctx.state.response.code}</Box> : undefined
        }
      >
        {responseTabs?.map(tab => (
          <TabItem
            label={tab.label}
            key={tab.label}
            isActive={tab.isActive ?? false}
            keepAlive={tab.keepAlive}
            disabled={tab.disabled}
          >
            {tab.content}
          </TabItem>
        ))}
      </Tabs>
    </FlexLayout>
  );
};

export default RequestExplorer;
